import React, { useState, useRef, useEffect } from "react";
import Icon from "../../utils/Icon";
import AnDropDown from "../../cta_utils/AnDropDown";
import { Tooltip } from "../../../design_system/components/ToolTip/ToolTip.jsx";
import ConditionalRender from "../../../ConditionalRender";
import { dateFormator, getMonthName, datepicker_input_format } from "../../utils/dateUtils";
import SingleCustomCalendarPicker from "../../cta_utils/DateCalanderDropdown";
import CustomCalendarPicker from "../../cta_utils/CalendarPopup";
import { Button } from "../../cta_utils/AnButton";

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
}

const SingleDateFilter = ({
  value,
  onChange,
  maxDate,
  customContainerClass = "",
  customStyle = {},
  tooltipText = "Select date"
}) => {
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState(value || null);
  const containerRef = useRef(null);
  const [calendarStartDate, setCalendarStartDate] = useState(tempDate || new Date());

  useEffect(() => {
    if (open) setTempDate(value || null);
  }, [open, value]);

  useClickOutside(containerRef, () => setOpen(false));

  const handleApply = (e) => {
    e.stopPropagation();
    onChange(tempDate);
    setOpen(false);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <Tooltip
        tooltipText={() => tooltipText}
        showWithoutOverflowing={true}
        placement="bottom"
      >
        <div
          className={customContainerClass}
          style={customStyle}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="CalendarIcon" className="white_image" />
        </div>
      </Tooltip>

      <ConditionalRender if={open}>
        <AnDropDown
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            background: "white",
            boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
            borderRadius: 8,
            zIndex: 100,
            width: 342
          }}
          toggleDropDown={() => setOpen(false)}
        >
          <div style={{ margin: "16px 24px 0" }}>
            <div
              style={{
                height: 36,
                backgroundColor: "#F3F4F5",
                color: tempDate ? "#000000" : "#B6B9BB",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: 4,
                display: "flex",
                alignItems: "center"
              }}
            >
              <span style={{ marginLeft: 12 }}>
                {tempDate?.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                }) || "Jun 1, 2022"}
              </span>
            </div>
          </div>

          <div style={{ margin: "0 24px 0 17px" }}>
            <SingleCustomCalendarPicker
              value={tempDate}
              onChange={setTempDate}
              calendarStartDate={calendarStartDate}
              setCalendarStartDate={setCalendarStartDate}
              selectRange={false}
            />
          </div>

          <div
            className="line_seperator_ex_light_grey"
            style={{ width: "100%", marginTop: 12 }}
          />

          <div
            className="default_flex_container"
            style={{
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 16,
              marginRight: 24
            }}
          >
            <Button
              buttonSize="Medium"
              type="Secondary"
              innerButtonStye={{
                width: 100,
                display: "flex",
                lineHeight: "20px",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 23.75
              }}
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button
              buttonSize="Medium"
              innerButtonStye={{
                width: 100,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: 23.75
              }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </AnDropDown>
      </ConditionalRender>
    </div>
  );
};

const SingleDateFilterDropdown = ({
  toggleDropDown,
  currDate,
  setCurrDate
}) => {
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const date = currDate ? new Date(currDate) : new Date();
    const tempCurrDate = dateFormator(date, { type: "datepicker_object" });
    setSelectedDay(tempCurrDate);
  }, []);

  const handleChange = (value) => {
    setSelectedDay(value);
    setCurrDate(value);
  };

  const [calendarStartDate, setCalendarStartDate] = useState(currDate || new Date());

  return (
    <AnDropDown
      style={{
        position: "absolute",
        marginTop: 4,
        background: "white",
        boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
        borderRadius: 8,
        zIndex: 100,
        minWidth: 340,
        width: 342,
      }}
      toggleDropDown={toggleDropDown}
    >
      <div style={{ margin: "24px" }}>
      <SingleCustomCalendarPicker
        value={currDate}
        onChange={handleChange}
        calendarStartDate={calendarStartDate}
        setCalendarStartDate={setCalendarStartDate}
        selectRange={false}
        selectedDate={selectedDay}
      />
      </div>
    </AnDropDown>
  );
};

export { SingleDateFilter, SingleDateFilterDropdown };

const RangeDateFilterDropdown = ({
  toggleDropDown,
  currDateRange,
  setCurrDateRange
}) => {
  const [calendarStartDate, setCalendarStartDate] = useState(
    currDateRange?.from || new Date()
  );

  useEffect(() => {
    if (currDateRange?.from) {
      setCalendarStartDate(currDateRange.from);
    }
  }, [currDateRange]);

  const handleChange = (range) => {
    const { from, to } = range;
    setCurrDateRange({ from, to });

    if (from && to) {
      toggleDropDown();
    }
  };

  return (
    <AnDropDown
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        marginTop: 4,
        background: "white",
        boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
        borderRadius: 8,
        zIndex: 100,
        minWidth: 340,
        width: 342,
      }}
      toggleDropDown={toggleDropDown}
    >
      <div style={{ margin: "24px" }}>
        <CustomCalendarPicker
          to={currDateRange?.to || null}
          from={currDateRange?.from || null}
          handleDateChange={(val) => {
            handleChange(val);
          }}
          calendarStartDate={calendarStartDate}
          setCalendarStartDate={setCalendarStartDate}
          selectRange={true}
        />
      </div>
    </AnDropDown>
  );
};

export { RangeDateFilterDropdown };
